<script lang="ts" setup>
  import { ref } from 'vue';
  import type { SpotifyPlaylist } from '../../../shared/api/playlists.ts';
  import { Button } from '../../../shared/ui/button';
  import { LogoText } from '../../../shared/ui/logo-text';
  import { MobileViewLayout } from '../../../shared/ui/mobile-view-layout';

  defineProps<{
    found: null | SpotifyPlaylist[];
    mine: SpotifyPlaylist[];
    notSignedIn: boolean;
    searchProblem?: string;
    searching: boolean;
  }>();

  const emit = defineEmits<{ search: [words: string] }>();

  const words = ref('');

  const submit = () => {
    if (words.value.trim() !== '') emit('search', words.value.trim());
  };

  const songsLabel = (playlist: SpotifyPlaylist): string =>
    playlist.songsReadable ? 'songs readable' : 'no songs from Spotify';
</script>

<template>
  <MobileViewLayout space="m">
    <LogoText class="title" text="Playlists" />

    <a v-if="notSignedIn" class="sign-in" href="/api/spotify/redirect">Sign in with Spotify to see your playlists</a>

    <template v-else>
      <section class="card">
        <h2>Your playlists</h2>
        <ul>
          <li v-for="playlist in mine" :key="playlist.id" :data-readable="playlist.songsReadable">
            <span class="name">{{ playlist.name }}</span>
            <span class="meta">{{ playlist.owner }} · {{ songsLabel(playlist) }}</span>
          </li>
        </ul>
      </section>

      <section class="card">
        <h2>Search any playlist</h2>
        <form @submit.prevent="submit">
          <input v-model="words" aria-label="playlist search" type="search" />
          <Button :disabled="searching" type="submit">search</Button>
        </form>
        <p v-if="searchProblem" role="alert">{{ searchProblem }}</p>
        <ul v-if="found !== null">
          <li v-for="playlist in found" :key="playlist.id" :data-readable="playlist.songsReadable">
            <span class="name">{{ playlist.name }}</span>
            <span class="meta">{{ playlist.owner }} · {{ songsLabel(playlist) }}</span>
          </li>
        </ul>
        <p v-if="found !== null && found.length === 0">No playlists found</p>
      </section>
    </template>
  </MobileViewLayout>
</template>

<style scoped>
  .title {
    font-size: var(--font-size-2xl);
  }

  .card {
    background: var(--color-surface);
    border-radius: var(--radius-card);
    box-shadow: 0 4px 12px var(--color-shadow);
    max-width: var(--control-max-width);
    padding: var(--card-padding);
    width: 100%;
  }

  h2 {
    margin: 0 0 0.5rem;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    display: flex;
    flex-direction: column;
  }

  .meta {
    opacity: 0.7;
  }

  li[data-readable='false'] .name {
    text-decoration: line-through;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  input {
    min-width: 0;
  }

  .sign-in {
    color: var(--color-primary);
  }
</style>
